import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../UI/card';
import { Badge } from '../UI/badge';
import { AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * COMPOSANT: PRÉVISUALISATION DES PAIEMENTS
 * 
 * Affiche un aperçu des paiements avant traitement
 */

const PREVIEW_DEFAULT = 10;

const PaymentPreview = ({ validationResults, type = 'third-party' }) => {
  const [showAllValid, setShowAllValid]     = useState(false);
  const [showAllInvalid, setShowAllInvalid] = useState(false);

  if (!validationResults) return null;

  // En V2, chaque ligne porte son propre mois/année — on affiche la période
  const showPeriod = type === 'bulk-v2';
  const fmtPeriod = (m, y) => {
    if (m == null && y == null) return '-';
    const mm = m != null ? String(m).padStart(2, '0') : '--';
    const yy = y != null ? String(y) : '----';
    return `${mm}/${yy}`;
  };

  const { totalRows, validRows, invalidRows, rows } = validationResults;
  const conformityRate = ((validRows / totalRows) * 100).toFixed(1);

  const formatAmount = (amount) => {
    const num = typeof amount === 'string'
      ? parseFloat(amount.replace(/[\s\u00A0]/g, '').replace(',', '.'))
      : Number(amount);
    return isNaN(num) ? (amount || 'N/A') : new Intl.NumberFormat('fr-FR').format(num) + ' FCFA';
  };

  const validList   = rows.filter(r => r.valid);
  const invalidList = rows.filter(r => !r.valid);
  const shownValid   = showAllValid   ? validList   : validList.slice(0, PREVIEW_DEFAULT);
  const shownInvalid = showAllInvalid ? invalidList : invalidList.slice(0, PREVIEW_DEFAULT);

  return (
    <div className="space-y-4">
      {/* Statistiques globales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 min-h-[120px] flex items-center justify-center">
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-sm text-muted-foreground mb-2">Total lignes</p>
              <p className="text-2xl font-bold">{totalRows}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4 min-h-[120px] flex items-center justify-center">
            <div className="flex flex-col items-center justify-center text-center">
              <CheckCircle className="w-6 h-6 text-green-600 mb-2" />
              <p className="text-sm text-green-700 mb-2">Valides</p>
              <p className="text-2xl font-bold text-green-600">{validRows}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 min-h-[120px] flex items-center justify-center">
            <div className="flex flex-col items-center justify-center text-center">
              <XCircle className="w-6 h-6 text-red-600 mb-2" />
              <p className="text-sm text-red-700 mb-2">Invalides</p>
              <p className="text-2xl font-bold text-red-600">{invalidRows}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 min-h-[120px] flex items-center justify-center">
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-sm text-blue-700 mb-2">Taux de conformité</p>
              <p className="text-2xl font-bold text-blue-600">{conformityRate}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des lignes invalides */}
      {invalidRows > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center text-red-600">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Lignes invalides ({invalidRows})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {shownInvalid.map((row, index) => (
                <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs font-semibold text-red-900">
                    Ligne {row.rowNumber} — {row.data.matricule || 'N/A'} · {row.data.nom || ''} {row.data.prenoms || ''} · {row.data.produit || 'N/A'} · {row.data.montant ? formatAmount(row.data.montant) : 'N/A'}
                    {showPeriod && (<> · <span className="font-mono">{fmtPeriod(row.data.month, row.data.year)}</span></>)}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {row.errors.map((err, i) => (
                      <li key={i} className="text-xs text-red-700 flex items-start gap-1">
                        <span>•</span><span>{err}</span>
                      </li>
                    ))}
                    {(row.warnings || []).map((w, i) => (
                      <li key={i} className="text-xs text-orange-600 flex items-start gap-1">
                        <span>⚠</span><span>{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {invalidList.length > PREVIEW_DEFAULT && (
              <button
                className="mt-2 w-full text-xs text-red-600 flex items-center justify-center gap-1 hover:underline"
                onClick={() => setShowAllInvalid(v => !v)}
              >
                {showAllInvalid
                  ? <><ChevronUp className="w-3 h-3" /> Réduire</>
                  : <><ChevronDown className="w-3 h-3" /> Voir les {invalidList.length - PREVIEW_DEFAULT} autres lignes invalides</>}
              </button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Liste des paiements valides */}
      {validRows > 0 && (
        <Card className="border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center text-green-600">
              <CheckCircle className="w-4 h-4 mr-2" />
              Lignes valides ({validRows})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-green-50">
                    <th className="text-left p-2 font-medium text-green-800">Ligne</th>
                    <th className="text-left p-2 font-medium text-green-800">Matricule</th>
                    <th className="text-left p-2 font-medium text-green-800">Nom & Prénoms</th>
                    <th className="text-left p-2 font-medium text-green-800">Produit</th>
                    {showPeriod && <th className="text-left p-2 font-medium text-green-800">Période</th>}
                    <th className="text-right p-2 font-medium text-green-800">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {shownValid.map((row, index) => (
                    <tr key={index} className="border-b hover:bg-green-50">
                      <td className="p-2 text-muted-foreground text-xs">
                        <div className="flex items-center gap-2">
                          {row.rowNumber}
                          {row.willBeAutoCreated && (
                            <span className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 rounded font-medium">
                              A créer
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-2 font-mono text-xs">{row.data.matricule}</td>
                      <td className="p-2">{row.data.nom} {row.data.prenoms}</td>
                      <td className="p-2 text-xs">{row.data.produit || 'N/A'}</td>
                      {showPeriod && <td className="p-2 text-xs font-mono">{fmtPeriod(row.data.month, row.data.year)}</td>}
                      <td className="p-2 text-right font-semibold">{formatAmount(row.data.montant)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {validList.length > PREVIEW_DEFAULT && (
              <button
                className="mt-2 w-full text-xs text-green-600 flex items-center justify-center gap-1 hover:underline"
                onClick={() => setShowAllValid(v => !v)}
              >
                {showAllValid
                  ? <><ChevronUp className="w-3 h-3" /> Réduire</>
                  : <><ChevronDown className="w-3 h-3" /> Voir les {validList.length - PREVIEW_DEFAULT} autres lignes valides</>}
              </button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentPreview;
